import { Box, IconButton, Paper, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { ELinks, formatPhoneNumber } from '@sharedInterfaces/globalEnums';
import FormatedTextInput from '@src/App/Components/General/FormularElements/formsControls/inputs/FormatedTextInput/FormatedTextInput';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import type Employee from '@src/Objects/Employee';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

interface AboutMeTabProps {
    employee: Employee;
    onTabChange?: (tabIndex: number) => void;
}

function AboutMeTab({ employee, onTabChange }: AboutMeTabProps) {
    const theme = useTheme();
    // const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const currentUser = useSelector((state: AppState) => state.employee);
    // const lang = useSelector((state: AppState) => state.employee.language);
    // const langStrings = getLanguageTexts(lang);
    const OUs = useSelector((state: AppState) => state.OUs);
    const employeeOU = OUs.find((ou) => ou.employees.includes(employee.id));

    return (
        <Box>
            {/* Header with Action Button */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    About Me
                </Typography>
                <IconButton
                    size="small"
                    onClick={() => navigate(`/${ELinks.PROFILE_EDIT}`)}
                    aria-label="Profil bearbeiten"
                    title="Profil bearbeiten"
                    sx={{
                        color: theme.palette.primary.main,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                        '&:hover': {
                            borderColor: theme.palette.primary.main,
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                        },
                    }}
                >
                    {getAppIcon(EIconCategory.EDIT)}
                </IconButton>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Kurzbeschreibung Card */}
                <Paper
                    elevation={0}
                    sx={{
                        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                        p: { lg: 3, md: 2, xs: 2 },
                    }}
                >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                        Kurzbeschreibung
                    </Typography>
                    {currentUser.description ? (
                        <FormatedTextInput value={currentUser.description} readonly={true} onChange={() => {}} />
                    ) : (
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                            Keine Beschreibung vorhanden
                        </Typography>
                    )}
                </Paper>

                {/* Kontakt Card */}
                <Paper
                    elevation={0}
                    sx={{
                        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                        p: { lg: 3, md: 2, xs: 2 },
                    }}
                >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                        Kontakt
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                            gap: 2,
                        }}
                    >
                        {/* Spalte 1: Basis-Kontaktdaten */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: (theme) => theme.shadows[4],
                                        borderColor: (theme) => theme.palette.primary.main,
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{ color: (theme) => theme.palette.primary.main }}>
                                        {getAppIcon(EIconCategory.CONTACT)}
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                            E-Mail
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {employee.email || 'Keine E-Mail angegeben'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: (theme) => theme.shadows[4],
                                        borderColor: (theme) => theme.palette.primary.main,
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{ color: (theme) => theme.palette.primary.main }}>
                                        {getAppIcon(EIconCategory.PHONE)}
                                    </Box>
                                    {employee.phoneNumber && (
                                        <Box>
                                            <Typography
                                                variant="caption"
                                                sx={{ color: 'text.secondary', fontWeight: 500 }}
                                            >
                                                Telefon
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {formatPhoneNumber(employee.phoneNumber)}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Paper>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: (theme) => theme.shadows[4],
                                        borderColor: (theme) => theme.palette.primary.main,
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{ color: (theme) => theme.palette.primary.main }}>
                                        {getAppIcon(EIconCategory.LOCATION)}
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                            Standort
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            Deutschland
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: (theme) => theme.shadows[4],
                                        borderColor: (theme) => theme.palette.primary.main,
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{ color: (theme) => theme.palette.primary.main }}>
                                        {getAppIcon(EIconCategory.DEPARTMENTS)}
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                            Abteilungsbereich
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {employeeOU?.title || 'Keine Abteilung zugeordnet'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        </Box>

                        {/* Spalte 2: Social & Links */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {employee.linkedInUrl && (
                                <Paper
                                    elevation={0}
                                    onClick={() => window.open(employee.linkedInUrl, '_blank')}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: (theme) => theme.shadows[4],
                                            borderColor: (theme) => theme.palette.primary.main,
                                        },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box sx={{ color: (theme) => theme.palette.primary.main }}>
                                            {getAppIcon(EIconCategory.LINKEDIN)}
                                        </Box>
                                        <Box>
                                            <Typography
                                                variant="caption"
                                                sx={{ color: 'text.secondary', fontWeight: 500 }}
                                            >
                                                LinkedIn
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 500, color: (theme) => theme.palette.primary.main }}
                                            >
                                                {employee.linkedInUrl}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            )}
                            {employee.slackUrl && (
                                <Paper
                                    elevation={0}
                                    onClick={() => window.open(employee.slackUrl, '_blank')}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: (theme) => theme.shadows[4],
                                            borderColor: (theme) => theme.palette.primary.main,
                                        },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box sx={{ color: (theme) => theme.palette.primary.main }}>
                                            {getAppIcon(EIconCategory.SLACK)}
                                        </Box>
                                        <Box>
                                            <Typography
                                                variant="caption"
                                                sx={{ color: 'text.secondary', fontWeight: 500 }}
                                            >
                                                Slack
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 500, color: (theme) => theme.palette.primary.main }}
                                            >
                                                {employee.slackUrl}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            )}
                            {employee.websiteUrl && (
                                <Paper
                                    elevation={0}
                                    onClick={() => window.open(employee.websiteUrl, '_blank')}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: (theme) => theme.shadows[4],
                                            borderColor: (theme) => theme.palette.primary.main,
                                        },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box sx={{ color: (theme) => theme.palette.primary.main }}>
                                            {getAppIcon(EIconCategory.WEBSITE)}
                                        </Box>
                                        <Box>
                                            <Typography
                                                variant="caption"
                                                sx={{ color: 'text.secondary', fontWeight: 500 }}
                                            >
                                                Website
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 500, color: (theme) => theme.palette.primary.main }}
                                            >
                                                {employee.websiteUrl}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            )}
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
}

export default AboutMeTab;
